import React, { useEffect, useState } from 'react';
import ReactSelect from 'react-select';

// COMPONENTS
import AutoComplete from '../../../src/components/Google/Autocomplete';

// TYPES
import { ExtendedCustomerRegistrationErrors, SignUpFormProps } from '../../../src/types/sign-up';

// STYLES
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import { useTranslations } from 'next-intl';

// UTILS
import listOfCountries from '../../../src/utils/listOfCountries';
import { hasAnyErrorMessage, hasErrorsInSection } from '../../../src/utils/sign-up';
import { useLazyQuery, useQuery } from '@apollo/client';
import { CHECK_IF_USER_EXISTS, GET_ALL_US_STATES } from '../../../src/lib/apollo';
import { formatPostalCode } from '@utils/formatPostalCode';
import { Country, CustomerType, QueryGetAllStatesArgs, State } from 'src/generated/graphql';
import { ButtonContainer } from '@components/TermsAndConditions/styles';

const SignupForm: React.FC<SignUpFormProps> = ({
  values,
  updateValue,
  populateValues,
  step,
  setStep,
  customerDetails,
  validateFields,
  errors,
  setErrors,
  updateValueManually,
}) => {
  const [googleData, setGoogleData] = useState<any>();

  const t = useTranslations("Register");

  const inputs = {
    city: 'city',
    country: 'country',
    street: 'line_1',
    zipCode: 'zipCode',
    phone: 'shippingPhone',
    email: 'shippingEmail',
    billingCity: 'billingCity',
    billingCountry: 'billingCountry',
    billingLine1: 'billingLine1',
    billingZipCode: 'billingZipCode',
    billingEmail: 'billingEmail',
    billingPhone: 'billingPhone',
    invoiceCity: 'invoiceCity',
    invoiceCountry: 'invoiceCountry',
    invoiceLine1: 'invoiceLine1',
    invoiceZipCode: 'invoiceZipCode',
    invoiceEmail: 'invoiceEmail',
    invoicePhone: 'invoicePhone',
  };

  const handleGoogleData = (data: any) => {
    const street = data?.find((item: any) => item.types[0] === 'route');
    const streetNumber = data?.find((item: any) => item.types[0] === 'street_number');
    const zipCode = data?.find((item: any) => item.types[0] === 'postal_code');
    const city = data?.find(
      (item: any) =>
        item.types[0] === 'postal_town' || item.types[0] === 'locality'
    );
    const country = data?.find((item: any) => item.types[0] === 'country');

    const updatedValues = { ...values };

    Object.values(inputs).forEach((input) => {
      if (
        input === 'line_1' ||
        input === 'billingLine1' ||
        input === 'invoiceLine1'
      ) {
        updatedValues[input] = `${street?.long_name} ${streetNumber?.long_name}`;
      } else if (
        input === 'zipCode' ||
        input === 'billingZipCode' ||
        input === 'invoiceZipCode'
      ) {
        updatedValues[input] = formatPostalCode(zipCode?.long_name || '');
      } else if (
        input === 'city' ||
        input === 'billingCity' ||
        input === 'invoiceCity'
      ) {
        updatedValues[input] = city?.long_name || '';
      } else if (
        input === 'country' ||
        input === 'billingCountry' ||
        input === 'invoiceCountry'
      ) {
        updatedValues[input] = {
          value: country?.short_name,
          label: country?.long_name,
        };
      }
    });

    populateValues({ ...updatedValues });
  };

  const [checkIfUserExists, { data: userExistsData }] =
    useLazyQuery(CHECK_IF_USER_EXISTS);

  useEffect(() => {
    if (googleData) {
      handleGoogleData(googleData);
    }
  }, [googleData]);

  const handleNext = (section: keyof ExtendedCustomerRegistrationErrors) => {
    const validationErrors = validateFields(values, t, section);
    setErrors(validationErrors);
    window.scrollTo(0, 0);

    if (!hasErrorsInSection(validationErrors, section)) {
      setStep(step + 1);
    }
  };

  useEffect(() => {
    if (userExistsData) {
      if (userExistsData.checkIfUserExists) {
        setErrors({
          ...errors,
          user: {
            email: 'Email already exists',
            password: '',
          },
        });
      } else {
        handleNext('user');
      }
    }
  }, [userExistsData]);

  const {
    name,
    storeName,
    shippingEmail,
    shippingPhone,
    attention,
    shippingPhoneCountryCode,
    line_1,
    line_2,
    city,
    zipCode,
    country,
    billingEmail,
    billingPhone,
    billingAttention,
    billingLine1,
    billingLine2,
    billingCity,
    billingZipCode,
    billingCountry,
    billingState,
    billingPhoneCountryCode,
    invoiceAttention,
    invoiceLine1,
    invoiceLine2,
    invoiceZipCode,
    invoiceCity,
    invoiceCountry,
    invoiceEmail,
    invoicePhone,

    invoicePhoneCountryCode,
  } = values;

  useEffect(() => {
    let updatedValues = { ...values };

    // Update address fields if the corresponding flags are true
    if (values.deliveryIsSameAsMain) {
      updatedValues = {
        ...updatedValues,
        line_1: billingLine1,
        line_2: billingLine2,
        city: billingCity,
        zipCode: formatPostalCode(billingZipCode),
        country: billingCountry,
        state: billingState,
        deliveryIsSameAsMain: true,
      };
    }

    if (values.invoiceIsSameAsMain) {
      updatedValues = {
        ...updatedValues,
        invoiceLine1: billingLine1,
        invoiceLine2: billingLine2,
        invoiceCity: billingCity,
        invoiceZipCode: formatPostalCode(billingZipCode),
        invoiceCountry: billingCountry,
        invoiceState: billingState,
        invoiceIsSameAsMain: true,
      };
    }

    // Update contact details if the corresponding flags are true
    if (values.invoiceContactIsSameAsMain) {
      updatedValues = {
        ...updatedValues,
        invoiceAttention: billingAttention,
        invoiceEmail: billingEmail,
        invoicePhoneCountryCode: billingPhoneCountryCode,
        invoicePhone: billingPhone,
        invoiceContactIsSameAsMain: true,
      };
    }

    if (values.deliveryContactIsSameAsMain) {
      updatedValues = {
        ...updatedValues,
        shippingEmail: billingEmail,
        shippingPhone: billingPhone,
        shippingPhoneCountryCode: billingPhoneCountryCode,
        storeName: name,
        attention: billingAttention,
        deliveryContactIsSameAsMain: true,
      };
    }

    populateValues(updatedValues);
  }, [
    billingLine1,
    billingLine2,
    billingCity,
    billingZipCode,
    billingCountry,
    billingState,
    billingAttention,
    billingEmail,
    billingPhone,
    billingPhoneCountryCode,
    name,
    values.deliveryIsSameAsMain,
    values.invoiceIsSameAsMain,
    values.invoiceContactIsSameAsMain,
    values.deliveryContactIsSameAsMain,
  ]);


  const { data: statesData } = useQuery(GET_ALL_US_STATES, {
    fetchPolicy: 'network-only',
  });

  const ValidCustomerTypes = [
    "OTHER",
    "MUSHER",
    "PET_STORE",
    "SPORT_STORE",
    "VETERINARY",
  ];

  const customerTypeImages = {
    [CustomerType.Other]: '/images/OTHER.webp',
    [CustomerType.Musher]: '/images/MUSHER.webp',
    [CustomerType.PetStore]: '/images/PET_STORE.webp',
    [CustomerType.SportStore]: '/images/SPORT_STORE.webp',
    [CustomerType.Veterinary]: '/images/VETERINARY.webp'
  };

  return (
    <>
      {/* Step 0: Customer Type Picker */}
      <div className={`fields-wrapper store-type ${step === 0 ? 'active' : ''}`}>
        <ul className="store-type-picker">
          {ValidCustomerTypes.map((type) => (
            <li
              key={type}
              onClick={() => {
                populateValues({
                  ...values,
                  customerType: {
                    value: type,
                    label:
                      type.charAt(0) +
                      type.slice(1).toLowerCase().split('_').join(' '),
                  },
                });
                setStep(step + 1);
                window.scrollTo(0, 0);
              }}
              style={{ listStyle: 'none', marginBottom: '1rem' }}
            >
              <img src={customerTypeImages[type]} alt={type} height="200" />
              <span>{t(`customerTypes.${type}`)}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Step 1: Your Details (User) */}
      <div className={`fields-wrapper grid-cols-1 ${step === 1 ? 'active' : ''}`}>
        {hasAnyErrorMessage(errors) && (
          <div className="error-message">
            <p>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-1"
                color="red"
                width={20}
                height={20}
              />
              {t('fixErrors')}
            </p>
          </div>
        )}
        <h2 className="span-2">{t('yourDetails')}</h2>
        <div className="form-control">
          <label htmlFor="email">{t('email')}:</label>
          <input
            required
            autoCapitalize="none"
            autoCorrect="off"
            name="email"
            id="email"
            type="email"
            value={customerDetails.email}
            onChange={(e) => {
              if (!customerDetails.shippingEmailEdited) {
                const dataToPopulate = {
                  ...values,
                  email: e.target.value,
                  shippingEmail: e.target.value,
                };
                if (!customerDetails.billingEmailEdited) {
                  dataToPopulate.billingEmail = e.target.value;
                }
                populateValues(dataToPopulate);
              } else {
                updateValue(e);
              }
            }}
          />
          {errors.user?.email && (
            <span className="error">{errors.user.email}</span>
          )}
        </div>
        <Buttons
          handleNext={handleNext}
          setStep={setStep}
          step={step}
          t={t}
          section="user"
        />
      </div>

      {/* Step 2: Company Details (Billing Address & Company Info) */}
      <div className={`fields-wrapper ${step === 2 ? 'active' : ''}`}>
        {hasAnyErrorMessage(errors) && (
          <div className="error-message">
            <p>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-1"
                color="red"
                width={20}
                height={20}
              />
              {t('fixErrors')}
            </p>
          </div>
        )}
        <h2 className="span-2">{t('companyDetails')}</h2>
        <h3 className="span-2 text-center">
          {t("awesomeNewStore", { customerType: customerDetails.customerType.label })}
        </h3>
        <div className="google-wrapper span-2 text-center">
          <p>{t('googleAutocompletePrompt')}</p>
          <AutoComplete disabled={false} setGoogleData={setGoogleData} />
        </div>
        <div className="form-control">
          <label htmlFor="name">
            {t('companyName')}:
            <input
              required
              name="name"
              id="name"
              type="text"
              value={customerDetails.name}
              onChange={(e) => {
                if (!customerDetails.nameEdited) {
                  populateValues({
                    ...values,
                    storeName: e.target.value,
                    name: e.target.value,
                  });
                } else {
                  updateValue(e);
                }
              }}
            />
            {errors.companyDetails?.name && (
              <span className="error">{errors.companyDetails.name}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="corporateID">
            {t('corporateID')}:
            <input
              name="corporateID"
              id="corporateID"
              type="text"
              value={customerDetails.corporateID}
              onChange={updateValue}
            />
            {errors.companyDetails?.corporateID && (
              <span className="error">{errors.companyDetails.corporateID}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="VATNumber">
            {t('vatNumber')}:
            <input
              name="VATNumber"
              id="VATNumber"
              type="text"
              value={customerDetails.VATNumber}
              onChange={updateValue}
            />
            {errors.companyDetails?.VATNumber && (
              <span className="error">{errors.companyDetails.VATNumber}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="billingLine1">
            {t('billingAddressLine1')}:
            <input
              required
              name="billingLine1"
              id="billingLine1"
              type="text"
              value={customerDetails.billingLine1}
              onChange={updateValue}
            />
            {errors.companyDetails?.billingLine1 && (
              <span className="error">{errors.companyDetails.billingLine1}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="billingCity">
            {t('billingCity')}:
            <input
              required
              name="billingCity"
              id="billingCity"
              type="text"
              value={customerDetails.billingCity}
              onChange={updateValue}
            />
            {errors.companyDetails?.billingCity && (
              <span className="error">{errors.companyDetails.billingCity}</span>
            )}
          </label>
        </div>
        {/* Added billing zip code input */}
        <div className="form-control">
          <label htmlFor="billingZipCode">
            {t('billingZipCode')}:
            <input
              required
              name="billingZipCode"
              id="billingZipCode"
              type="text"
              value={customerDetails.billingZipCode}
              onChange={updateValue}
            />
            {errors.companyDetails?.billingZipCode && (
              <span className="error">{errors.companyDetails.billingZipCode}</span>
            )}
          </label>
        </div>


        <div className="form-control">
          <label htmlFor="billingCountry">
            {t("billingCountry")}
            <ReactSelect
              className="react-select"
              name="billingCountry"
              id="billingCountry"
              options={listOfCountries}
              value={customerDetails.billingCountry}
              onChange={(e) =>
                updateValueManually("billingCountry", {
                  value: e?.value || "",
                  label: e?.label || "",
                })
              }
            />
            {errors.companyDetails?.billingCountry && (
              <span className="error">{errors.companyDetails.billingCountry}</span>
            )}
          </label>
        </div>

        {(billingCountry.value === "US" || billingCountry.value === "CN") && (
          <div className="form-control">
            <label htmlFor="billingState">
              {t("billingState")}
              <ReactSelect
                className="react-select"
                name="billingState"
                id="billingState"
                options={statesData?.getStates?.filter(state => state?.country?.code === billingCountry.value).map((state) => {
                  return {
                    value: state?.code || "",
                    label: state?.name || "",
                  };
                })}
                value={customerDetails.billingState}
                onChange={(e) =>
                  updateValueManually("billingState", {
                    value: e?.value || "",
                    label: e?.label || "",
                  })
                }
              />
              {errors.companyDetails?.billingState && (
                <span className="error">{errors.companyDetails.billingState}</span>
              )}
            </label>
          </div>
        )}
        <div className="form-control">
          <label htmlFor="applicantComment">
            {t("additionalComments")}
            <textarea
              name="applicantComment"
              id="applicantComment"
              rows={10}
              value={customerDetails.applicantComment}
              onChange={updateValue}
            ></textarea>
          </label>
        </div>
        <Buttons
          handleNext={handleNext}
          setStep={setStep}
          step={step}
          t={t}
          section="companyDetails"
        />
      </div>

      {/* Step 3: Billing Contact */}
      <div className={`fields-wrapper ${step === 3 ? 'active' : ''}`}>
        <div className="form-control">
          <label htmlFor="billingEmail">
            {t('billingEmail')}:
            <input
              name="billingEmail"
              id="billingEmail"
              type="text"
              value={customerDetails.billingEmail}
              onChange={(e) => {
                if (!customerDetails.billingEmailEdited) {
                  populateValues({
                    ...values,
                    shippingEmailEdited: true,
                    shippingEmail: e.target.value,
                    billingEmail: e.target.value,
                  });
                } else {
                  updateValue(e);
                }
              }}
            />
            {errors.companyContact?.billingEmail && (
              <span className="error">{errors.companyContact.billingEmail}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="billingAttention">
            {t("billingAttention")}:
            <input
              name="billingAttention"
              id="billingAttention"
              type="text"
              value={customerDetails.billingAttention}
              onChange={(e) => {
                populateValues({
                  ...values,
                  billingAttention: e.target.value,
                });
              }}
            />
            {errors.companyContact?.billingAttention && (
              <span className="error">{errors.companyContact.billingAttention}</span>
            )}
          </label>
        </div>
        <div className="form-control phone-select">
          <p className="form-field-title">{t("billingPhone")}</p>
          <ReactSelect
            className="react-select"
            id="billing-country-code"
            isSearchable
            name="billing-country-code"
            onChange={(e) => {
              updateValueManually("billingPhoneCountryCode", {
                value: e?.value || "",
                label: e?.label || "",
              });
            }}
            options={listOfCountries.map((country) => ({
              value: country.dial_code,
              label: `${country.value} ${country.label} (${country.dial_code})`,
            }))}
          />
          <label>
            {t("noCountryCode")}
            <input
              name="billingPhone"
              id="billingPhone"
              type="number"
              value={customerDetails.billingPhone}
              onChange={(e) => {
                if (!customerDetails.shippingPhoneEdited) {
                  populateValues({
                    ...values,
                    shippingPhone: e.target.value,
                    billingPhone: e.target.value,
                  });
                } else {
                  updateValue(e);
                }
              }}
            />
          </label>
          {errors.companyContact?.billingPhone && (
            <span className="error ">{errors.companyContact.billingPhone}</span>
          )}
        </div>
        <Buttons
          handleNext={handleNext}
          setStep={setStep}
          step={step}
          t={t}
          section="companyContact"
        />
      </div>

      <div className={`fields-wrapper ${step === 4 ? 'active' : ''}`}>
        {hasAnyErrorMessage(errors) && (
          <div className="error-message">
            <p>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-1"
                color="red"
                width={20}
                height={20}
              />
              {t("fixErrors")}
            </p>
          </div>
        )}
        <h2 className="span-2">{t("storeDetails")}</h2>

        {/* Checkbox for deliveryIsSameAsMain */}
        <div className="form-control span-2">
          <label className="flex flex-row items-center gap-1">
            <input
              type="checkbox"
              name="deliveryIsSameAsMain"
              id="deliveryIsSameAsMain"
              checked={values.deliveryIsSameAsMain}
              onChange={(e) =>
                populateValues({
                  ...values,
                  deliveryIsSameAsMain: e.target.checked,
                })
              }
            />
            {t("sameAsCompanyAddress")}
          </label>
        </div>

        <div className="form-control">
          <label htmlFor="line_1">
            {t("shippingAddressLine1")}:
            <input
              required
              name="line_1"
              id="line_1"
              type="text"
              // use the destructured value here
              value={line_1}
              onChange={(e) => {
                populateValues({
                  ...values,
                  line_1: e.target.value,
                  deliveryIsSameAsMain: false,
                });
              }}
            />
            {errors.storeDetails?.line_1 && (
              <span className="error">{errors.storeDetails.line_1}</span>
            )}
          </label>
        </div>

        <div className="form-control">
          <label htmlFor="line_2">
            {t("shippingAddressLine2")}:
            <input
              name="line_2"
              id="line_2"
              type="text"
              value={line_2}
              onChange={(e) => {
                populateValues({
                  ...values,
                  line_2: e.target.value,
                  deliveryIsSameAsMain: false,
                });
              }}
            />
            {errors.storeDetails?.line_2 && (
              <span className="error">{errors.storeDetails.line_2}</span>
            )}
          </label>
        </div>

        <div className="form-control">
          <label htmlFor="city">
            {t("shippingCity")}:
            <input
              required
              name="city"
              id="city"
              type="text"
              value={city}
              onChange={(e) => {
                populateValues({
                  ...values,
                  city: e.target.value,
                  deliveryIsSameAsMain: false,
                });
              }}
            />
            {errors.storeDetails?.city && (
              <span className="error">{errors.storeDetails.city}</span>
            )}
          </label>
        </div>

        <div className="form-control">
          <label htmlFor="zipCode">
            {t("shippingZipCode")}:
            <input
              required
              name="zipCode"
              id="zipCode"
              type="text"
              value={zipCode}
              onChange={(e) => {
                populateValues({
                  ...values,
                  zipCode: e.target.value,
                  deliveryIsSameAsMain: false,
                });
              }}
            />
            {errors.storeDetails?.zipCode && (
              <span className="error">{errors.storeDetails.zipCode}</span>
            )}
          </label>
        </div>

        <div className="form-control">
          <label htmlFor="country">
            {t("shippingCountry")}:
            <ReactSelect
              className="react-select"
              name="country"
              id="country"
              options={listOfCountries}
              value={country}
              onChange={(e) =>
                populateValues({
                  ...values,
                  country: {
                    value: e?.value || "",
                    label: e?.label || "",
                  },
                  deliveryIsSameAsMain: false,
                })
              }
            />
            {errors.storeDetails?.country && (
              <span className="error">{errors.storeDetails.country}</span>
            )}
          </label>
        </div>


        {(country.value === "US" || country.value === "CN") && (
          <div className="form-control">
            <label htmlFor="state">
              {t("state")}
              <ReactSelect
                className="react-select"
                name="state"
                id="state"
                options={statesData?.getStates?.filter(state => state?.country?.code === billingCountry.value).map((state) => {
                  return {
                    value: state?.code || "",
                    label: state?.name || "",
                  };
                })}
                value={customerDetails.state}
                onChange={(e) =>
                  populateValues({
                    ...values,
                    state: {
                      value: e?.value || "",
                      label: e?.label || "",
                    },
                    deliveryIsSameAsMain: false,
                  })
                }
              />
              {errors.storeDetails?.state && (
                <span className="error">{errors.storeDetails.state}</span>
              )}
            </label>
          </div>
        )}

        <Buttons
          handleNext={handleNext}
          setStep={setStep}
          step={step}
          t={t}
          section="storeDetails"
        />
        <p className="span-2 text-center">
          This is the address of the store that will receive the Non-stop products you order
        </p>
      </div>

      {/* Step 5: Store Contact (Shipping Contact) */}
      <div className={`fields-wrapper ${step === 5 ? 'active' : ''}`}>
        <div className="form-control span-2">
          <label className="flex flex-row items-center gap-1">
            <input
              className="mr-1"
              type="checkbox"
              name="deliveryContactIsSameAsMain"
              id="deliveryContactIsSameAsMain"
              checked={values.deliveryContactIsSameAsMain}
              onChange={(e) => {
                populateValues({
                  ...values,
                  deliveryContactIsSameAsMain: e.target.checked,
                });
              }}
            />
            {t("sameAsCompanyContact")}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="storeName">
            {t("storeName")}:
            <input
              required
              name="storeName"
              id="storeName"
              type="text"
              value={storeName}
              onChange={(e) => {
                populateValues({
                  ...values,
                  storeName: e.target.value,
                  deliveryContactIsSameAsMain: false,
                });
              }}
            />
            {errors.storeContact?.storeName && (
              <span className="error">{errors.storeContact.storeName}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="attention">
            {t("attention")}:
            <input
              name="attention"
              id="attention"
              type="text"
              value={attention}
              onChange={(e) => {
                populateValues({
                  ...values,
                  attention: e.target.value,
                  deliveryContactIsSameAsMain: false,
                });
              }}
            />
            {errors.storeContact?.attention && (
              <span className="error">{errors.storeContact.attention}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="shippingEmail">
            {t("shippingEmail")}:
            <input
              name="shippingEmail"
              id="shippingEmail"
              type="text"
              value={shippingEmail}
              onChange={(e) => {
                populateValues({
                  ...values,
                  shippingEmailEdited: true,
                  shippingEmail: e.target.value,
                  deliveryContactIsSameAsMain: false,
                });
              }}
            />
            {errors.storeContact?.shippingEmail && (
              <span className="error">{errors.storeContact.shippingEmail}</span>
            )}
          </label>
        </div>
        <div className="form-control phone-select">
          <p className="form-field-title">{t("shippingPhone")}</p>
          <ReactSelect
            className="react-select"
            id="country-code"
            isSearchable
            value={shippingPhoneCountryCode}
            name="country-code"
            onChange={(e) => {
              populateValues({
                ...values,
                deliveryContactIsSameAsMain: false,
                shippingPhoneCountryCode: {
                  value: e?.value || "",
                  label: e?.label || "",
                }
              });
            }}
            options={listOfCountries.map((country) => ({
              value: country.dial_code,
              label: `${country.value} ${country.label} (${country.dial_code})`,
            }))}
          />
          <label>
            {t("noCountryCode")}
            <input
              name="shippingPhone"
              id="shippingPhone"
              type="number"
              value={shippingPhone}
              onChange={(e) => {
                populateValues({
                  ...values,
                  shippingPhoneEdited: true,
                  shippingPhone: e.target.value,
                  deliveryContactIsSameAsMain: false,
                });
              }}
            />
          </label>
          {errors.storeContact?.shippingPhone && (
            <span className="error col-span-2">{errors.storeContact.shippingPhone}</span>
          )}
        </div>
        <Buttons
          handleNext={handleNext}
          setStep={setStep}
          step={step}
          t={t}
          section="storeContact"
        />
      </div>

      {/* Step 6: Invoice Details (Invoice Address) */}
      <div className={`fields-wrapper ${step === 6 ? 'active' : ''}`}>
        {hasAnyErrorMessage(errors) && (
          <div className="error-message">
            <p>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-1"
                color="red"
                width={20}
                height={20}
              />
              {t("fixErrors")}
            </p>
          </div>
        )}
        <h2 className="span-2">{t("invoiceDetails")}</h2>
        <div className="form-control span-2">
          <label className="flex flex-row items-center gap-1">
            <input
              className="mr-1"
              type="checkbox"
              name="invoiceIsSameAsMain"
              id="invoiceIsSameAsMain"
              checked={values.invoiceIsSameAsMain}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceIsSameAsMain: e.target.checked,
                });
              }}
            />
            {t("sameAsCompanyAddress")}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="invoiceLine1">
            {t("invoiceAddressLine1")}:
            <input
              required
              name="invoiceLine1"
              id="invoiceLine1"
              type="text"
              value={invoiceLine1}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceIsSameAsMain: false,
                  invoiceLine1: e.target.value,
                });
              }}
            />
            {errors.invoiceDetails?.invoiceLine1 && (
              <span className="error">{errors.invoiceDetails.invoiceLine1}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="invoiceLine2">
            {t("invoiceAddressLine2")}:
            <input
              name="invoiceLine2"
              id="invoiceLine2"
              type="text"
              value={invoiceLine2}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceIsSameAsMain: false,
                  invoiceLine2: e.target.value,
                });
              }}
            />
            {errors.invoiceDetails?.invoiceLine2 && (
              <span className="error">{errors.invoiceDetails.invoiceLine2}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="invoiceZipCode">
            {t("invoiceZipCode")}:
            <input
              required
              name="invoiceZipCode"
              id="invoiceZipCode"
              type="text"
              value={invoiceZipCode}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceIsSameAsMain: false,
                  invoiceZipCode: e.target.value,
                });
              }}
            />
            {errors.invoiceDetails?.invoiceZipCode && (
              <span className="error">{errors.invoiceDetails.invoiceZipCode}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="invoiceCity">
            {t("invoiceCity")}:
            <input
              required
              name="invoiceCity"
              id="invoiceCity"
              type="text"
              value={invoiceCity}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceIsSameAsMain: false,
                  invoiceCity: e.target.value,
                });
              }}
            />
            {errors.invoiceDetails?.invoiceCity && (
              <span className="error">{errors.invoiceDetails.invoiceCity}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="invoiceCountry">
            {t("invoiceCountry")}:
            <ReactSelect
              className="react-select"
              name="invoiceCountry"
              id="invoiceCountry"
              options={listOfCountries}
              value={invoiceCountry}
              onChange={(e) =>
                populateValues({
                  ...values,
                  invoiceCountry: {
                    value: e?.value || "",
                    label: e?.label || "",
                  },
                  invoiceIsSameAsMain: false,
                })
              }
            />
            {errors.invoiceDetails?.invoiceCountry && (
              <span className="error">{errors.invoiceDetails.invoiceCountry}</span>
            )}
          </label>
        </div>


        {country.value === "US" || country.value === "CN"
          && (
            <div className="form-control">
              <label htmlFor="invoiceState">
                {t("invoiceState")}
                <ReactSelect
                  className="react-select"
                  name="invoiceState"
                  id="invoiceState"
                  options={statesData?.getStates?.filter(state => state?.country?.code === billingCountry.value).map((state) => {
                    return {
                      value: state?.code || "",
                      label: state?.name || "",
                    };
                  })}
                  value={customerDetails.invoiceState}
                  onChange={(e) =>
                    populateValues({
                      ...values,
                      invoiceState: {
                        value: e?.value || "",
                        label: e?.label || "",
                      },
                      invoiceIsSameAsMain: false,
                    })
                  }
                />
                {errors.invoiceDetails?.invoiceState && (
                  <span className="error">{errors.invoiceDetails.invoiceState}</span>
                )}
              </label>
            </div>
          )}

        <Buttons
          handleNext={handleNext}
          setStep={setStep}
          step={step}
          t={t}
          section="invoiceDetails"
        />
      </div>

      {/* Step 7: Invoice Contact */}
      <div className={`fields-wrapper ${step === 7 ? 'active' : ''}`}>
        <div className="form-control span-2">
          <label className="flex flex-row items-center gap-1">
            <input
              className="mr-1"
              type="checkbox"
              name="invoiceContactIsSameAsMain"
              id="invoiceContactIsSameAsMain"
              checked={values.invoiceContactIsSameAsMain}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceContactIsSameAsMain: e.target.checked,
                });
              }}
            />
            {t("sameAsCompanyContact")}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="invoiceAttention">
            {t("invoiceAttention")}:
            <input
              name="invoiceAttention"
              id="invoiceAttention"
              type="text"
              value={invoiceAttention}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceContactIsSameAsMain: false,
                  invoiceAttention: e.target.value,
                });
              }}
            />
            {errors.invoiceContact?.invoiceAttention && (
              <span className="error">{errors.invoiceContact.invoiceAttention}</span>
            )}
          </label>
        </div>
        <div className="form-control">
          <label htmlFor="invoiceEmail">
            {t("invoiceEmail")}:
            <input
              name="invoiceEmail"
              id="invoiceEmail"
              type="text"
              value={invoiceEmail}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceContactIsSameAsMain: false,
                  invoiceEmail: e.target.value,
                });
              }}
            />
            {errors.invoiceContact?.invoiceEmail && (
              <span className="error">{errors.invoiceContact.invoiceEmail}</span>
            )}
          </label>
        </div>
        <div className="form-control phone-select">
          <p className="form-field-title">{t("invoicePhone")}</p>
          <ReactSelect
            className="react-select"
            id="invoicePhoneCountryCode"
            isSearchable
            value={values.invoicePhoneCountryCode}
            name="invoicePhoneCountryCode"
            onChange={(e) => {
              populateValues({
                ...values,
                invoiceContactIsSameAsMain: false,
                invoicePhoneCountryCode: {
                  value: e?.value || "",
                  label: e?.label || "",
                }
              });
            }}
            options={listOfCountries.map((country) => ({
              value: country.dial_code,
              label: `${country.value} ${country.label} (${country.dial_code})`,
            }))}
          />
          <label>
            {t("noCountryCode")}
            <input
              name="invoicePhone"
              id="invoicePhone"
              type="number"
              value={invoicePhone}
              onChange={(e) => {
                populateValues({
                  ...values,
                  invoiceContactIsSameAsMain: false,
                  invoicePhone: e.target.value,
                });
              }}
            />
          </label>
          {errors.invoiceContact?.invoicePhone && (
            <span className="error col-span-2">{errors.invoiceContact.invoicePhone}</span>
          )}
        </div>
        <div className="span-2">
          <div className="buttons-container">
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => setStep(step - 1)}
            >
              {t("back")}
            </button>
            <button className="btn btn-success" type="submit">
              {t("submit")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const Buttons = ({ setStep, step, handleNext, t, section }: any) => {
  return (
    <div className="buttons-container desktop-only span-2">
      <a className="" onClick={() => {
        setStep(step - 1)

        window.scrollTo(0, 0);
      }}>
        {t("back")}
      </a>
      <button
        className="btn btn-primary"
        type="button"
        onClick={() => handleNext(section)}
      >
        {t("next")}
      </button>
    </div>
  );
};

export default SignupForm;
